import type { NavigationItem, NavigationGroup, UserContext, Permission, UserRole } from './types';

/**
 * Check if user has required role
 */
export const hasRequiredRole = (
  userRole: UserRole,
  requiredRoles?: UserRole[],
  excludeRoles?: UserRole[]
): boolean => {
  if (excludeRoles?.includes(userRole)) {
    return false;
  }
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }
  return requiredRoles.includes(userRole);
};

/**
 * Check if user has required permissions
 */
export const hasRequiredPermissions = (
  userPermissions: Permission[],
  requiredPermissions?: Permission[]
): boolean => {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }
  return requiredPermissions.every(permission => 
    userPermissions.includes(permission)
  );
};

/**
 * Check if user has required feature flags
 */
export const hasRequiredFeatures = (
  userFeatures: string[],
  requiredFeatures?: string[]
): boolean => {
  if (!requiredFeatures || requiredFeatures.length === 0) {
    return true;
  }
  return requiredFeatures.every(feature => 
    userFeatures.includes(feature)
  );
};

/**
 * A delegable item the user reaches through an ACTIVE delegation: they hold
 * every required permission as a delegated capability (DEF-0mvpyq10d005, NA-3).
 * The role is not asked — a delegate is usually not a manager — but an item
 * with no permission to delegate never opens this way.
 */
export const isDelegatedAccessible = (
  item: NavigationItem,
  context: UserContext
): boolean => {
  const delegated = context.delegatedPermissions ?? [];
  return (
    !!item.delegable &&
    !!item.requiredPermissions?.length &&
    delegated.length > 0 &&
    !item.excludeRoles?.includes(context.role) &&
    hasRequiredPermissions(delegated, item.requiredPermissions) &&
    hasRequiredFeatures(context.featureFlags, item.requiredFeatures)
  );
};

/**
 * An `anyTier` item whose permissions and features hold, whatever the user's
 * nav tier (an owner who is also a Brand Ambassador, WP-2d). The tier is not
 * asked, but excludeRoles still is.
 */
export const isTierFreeAccessible = (
  item: NavigationItem,
  context: UserContext
): boolean =>
  !!item.anyTier &&
  !item.excludeRoles?.includes(context.role) &&
  hasRequiredPermissions(context.permissions, item.requiredPermissions) &&
  hasRequiredFeatures(context.featureFlags, item.requiredFeatures);

/**
 * Check if navigation item is accessible to user
 */
export const isItemAccessible = (
  item: NavigationItem,
  context: UserContext
): boolean => {
  return (
    (hasRequiredRole(context.role, item.requiredRoles, item.excludeRoles) &&
      hasRequiredPermissions(context.permissions, item.requiredPermissions) &&
      hasRequiredFeatures(context.featureFlags, item.requiredFeatures)) ||
    isDelegatedAccessible(item, context) ||
    isTierFreeAccessible(item, context)
  );
};

/**
 * Filter navigation items based on user context
 */
export const filterNavigationItems = (
  items: NavigationItem[],
  context: UserContext
): NavigationItem[] => {
  return items
    .filter(item => isItemAccessible(item, context))
    .map(item => {
      if (item.children) {
        return {
          ...item,
          children: filterNavigationItems(item.children, context)
        };
      }
      return item;
    })
    .filter(item => !item.children || item.children.length > 0);
};

/**
 * Filter navigation groups based on user context
 */
export const filterNavigationGroups = (
  groups: NavigationGroup[],
  context: UserContext
): NavigationGroup[] => {
  return groups
    .filter(group =>
      (hasRequiredRole(context.role, group.requiredRoles) &&
        hasRequiredPermissions(context.permissions, group.requiredPermissions)) ||
      // A delegate reaches the group only for its delegable items (the
      // item filter below then drops everything else in it).
      group.items.some(item => isDelegatedAccessible(item, context) || isTierFreeAccessible(item, context))
    )
    .map(group => ({
      ...group,
      items: filterNavigationItems(group.items, context)
    }))
    .filter(group => group.items.length > 0)
    .sort((a, b) => a.order - b.order);
};

/**
 * Get available dashboards for user
 */
export const getAvailableDashboards = (
  dashboards: NavigationItem[],
  context: UserContext
): NavigationItem[] => {
  return dashboards.filter(dashboard => 
    isItemAccessible(dashboard, context)
  );
};

/**
 * Get default dashboard for user role
 */
export const getDefaultDashboard = (
  dashboards: NavigationItem[],
  userRole: UserRole
): NavigationItem | undefined => {
  return dashboards.find(dashboard => 
    dashboard.dashboardMetadata?.defaultFor?.includes(userRole)
  );
};

/**
 * Build path with tenant slug
 */
export const buildTenantPath = (
  path: string,
  tenantSlug?: string,
  isSystemAdmin: boolean = false
): string => {
  // Handle empty path (usually for dashboard root)
  const cleanPath = path || '';
  
  if (isSystemAdmin) {
    return `/admin${cleanPath ? `/${cleanPath}` : ''}`;
  }
  
  if (tenantSlug) {
    return `/t/${tenantSlug}/app${cleanPath ? `/${cleanPath}` : ''}`;
  }
  
  return `/app${cleanPath ? `/${cleanPath}` : ''}`;
};

/**
 * Extract tenant slug from pathname
 */
export const extractTenantSlug = (pathname: string): string | undefined => {
  const match = pathname.match(/^\/t\/([^/]+)/);
  return match ? match[1] : undefined;
};

/**
 * Check if current path matches navigation item
 */
export const isPathActive = (
  currentPath: string,
  itemPath: string,
  tenantSlug?: string,
  isSystemAdmin: boolean = false
): boolean => {
  const fullItemPath = buildTenantPath(itemPath, tenantSlug, isSystemAdmin);
  
  // Exact match for root paths
  if (fullItemPath === currentPath) {
    return true;
  }
  
  // Check if current path starts with item path (for nested routes)
  // But not for root path to avoid matching everything
  if (itemPath && currentPath.startsWith(fullItemPath + '/')) {
    return true;
  }
  
  return false;
};